import { useState, useEffect, useMemo } from "react";
import { permissionsApi } from "../../../components/PermissionsManagement/services/permissionsApi";
import { processPermissionsData } from "../../../components/PermissionsManagement/utils/permissionsProcessor";

/**
 * Manages permission data loading, caching, and processing
 */
export const usePermissionsData = (
  selectedGroup,
  permissionsCache,
  setPermissionsCache,
  activeSubTab,
  formsContentType
) => {
  const [rawApiData, setRawApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingFromToolbar, setSavingFromToolbar] = useState(false);
  const [savingFromModal, setSavingFromModal] = useState(false);
  const [error, setError] = useState(null);
  const [allProcessedStructures, setAllProcessedStructures] = useState({});

  const groupId = selectedGroup?.id;
  const groupName = selectedGroup?.name;

  // Process all permission structures when raw data changes
  useEffect(() => {
    if (!rawApiData) {
      setAllProcessedStructures({});
      return;
    }

    const structures = processAllStructures(rawApiData);
    setAllProcessedStructures(structures);
  }, [rawApiData]);

  // Get current processed structure based on active tab
  const processedPermissions = useMemo(() => {
    const key =
      activeSubTab === "forms_content"
        ? `forms_content_${formsContentType}`
        : activeSubTab;

    const result = allProcessedStructures[key] || null;

    return result;
  }, [allProcessedStructures, activeSubTab, formsContentType]);

  // Load permissions when group changes
  useEffect(() => {
    if (!groupId || !groupName) {
      clearData();
      return;
    }

    // Check cache validity
    const cacheIsValid = isCacheValid(permissionsCache, groupId, groupName);
    if (cacheIsValid) {
      setRawApiData(permissionsCache.data);
    } else {
      loadPermissionsForGroup(groupId, groupName);
    }
  }, [groupId, groupName]);

  // Clear data when group is deselected
  useEffect(() => {
    if (!selectedGroup) {
      clearData();
    }
  }, [selectedGroup]);

  const loadPermissionsForGroup = async (groupId, groupName) => {
    setLoading(true);
    setError(null);

    // Clear any existing data first
    setRawApiData(null);

    try {
      const data = await permissionsApi.loadPermissions(groupId, groupName);

      // Verify this is still the selected group
      if (selectedGroup?.id === groupId && selectedGroup?.name === groupName) {
        setRawApiData(data);
        setPermissionsCache({ groupId, groupName, data });
      } else {
      }
    } catch (error) {
      console.error("❌ FAILED TO LOAD PERMISSIONS:", error);
      setError(error.message || "Failed to load permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleSetSaving = (isSaving, source = "button") => {
    if (source === "toolbar") {
      setSavingFromToolbar(isSaving);
    } else if (source === "modal") {
      setSavingFromModal(isSaving);
    } else {
      setSaving(isSaving);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const clearData = () => {
    setRawApiData(null);
    setError(null);
    setAllProcessedStructures({});
  };

  const getProcessedStructure = (tabName, contentType = null) => {
    const key =
      tabName === "forms_content"
        ? `forms_content_${contentType || "general"}`
        : tabName;
    const structure = allProcessedStructures[key] || null;
    return structure;
  };

  const updateRawDataFromTrackedChanges = (trackedChanges) => {
    if (!rawApiData || !trackedChanges || trackedChanges.size === 0) return;

    // Create a deep copy of the raw data
    const updatedRawData = JSON.parse(JSON.stringify(rawApiData));

    // Apply each tracked change to the raw data
    trackedChanges.forEach((change) => {
      const { itemId, permissionType, currentValue } = change;

      // Parse the item ID to numeric format
      let numericItemId = itemId;
      if (typeof itemId === "string") {
        if (itemId.startsWith("form_")) {
          const parts = itemId.split("_");
          numericItemId = parseInt(parts[1]);
        } else if (!isNaN(itemId)) {
          numericItemId = parseInt(itemId);
        }
      }

      // Find and update the item in the appropriate table
      const tables = [
        updatedRawData.formS_TABLE,
        updatedRawData.formS_GEN_DETAILS_TABLE,
        updatedRawData.formS_TABS_TABLE,
        updatedRawData.miscellaneouS_TABLE,
      ].filter(Boolean);

      for (const table of tables) {
        const item = table?.find((item) => item.itemid === numericItemId);
        if (item) {
          // Update the appropriate permission field
          switch (permissionType) {
            case "search_view":
            case "view_open":
            case "create_view":
            case "enabled":
              item.caN_SEARCH = currentValue ? 1 : 0;
              if (item.hasOwnProperty("view")) {
                item.view = currentValue ? 1 : 0;
              }
              break;
            case "view":
              item.canview = currentValue ? 1 : 0;
              if (item.hasOwnProperty("view")) {
                item.view = currentValue ? 1 : 0;
              }
              break;
            case "create":
              item.caN_CREATE = currentValue ? 1 : 0;
              if (item.hasOwnProperty("crt")) {
                item.crt = currentValue ? 1 : 0;
              }
              break;
            case "edit":
              item.caN_EDIT = currentValue ? 1 : 0;
              if (item.hasOwnProperty("edt")) {
                item.edt = currentValue ? 1 : 0;
              }
              break;
            case "delete":
              item.caN_DELETE = currentValue ? 1 : 0;
              if (item.hasOwnProperty("dlt")) {
                item.dlt = currentValue ? 1 : 0;
              }
              break;
            case "print":
              item.canprint = currentValue ? 1 : 0;
              if (item.hasOwnProperty("print")) {
                item.print = currentValue ? 1 : 0;
              }
              break;
          }
          break; // Found and updated the item, no need to check other tables
        }
      }
    });

    // Update the raw data state
    setRawApiData(updatedRawData);

    // Update the cache with the new baseline
    if (selectedGroup) {
      setPermissionsCache({
        groupId: selectedGroup.id,
        groupName: selectedGroup.name,
        data: updatedRawData,
      });
    }
  };

  return {
    rawApiData,
    processedPermissions,
    allProcessedStructures,
    getProcessedStructure,
    loading,
    saving, // This is for bottom button saves only
    savingFromToolbar, // This is for toolbar saves only
    savingFromModal, // This is for modal saves (no UI indicators)
    setSaving: handleSetSaving,
    error,
    setError,
    clearError,
    loadPermissionsForGroup,
    updateRawDataFromTrackedChanges, // Export the new function
  };
};

/**
 * Process all permission structures upfront for better performance
 */
function processAllStructures(rawApiData) {
  const structures = {};

  structures.forms = processPermissionsData(rawApiData, "forms");
  structures.forms_content_general = processPermissionsData(
    rawApiData,
    "forms_content",
    "general"
  );
  structures.forms_content_tabs = processPermissionsData(
    rawApiData,
    "forms_content",
    "tabs"
  );
  structures.tools = processPermissionsData(rawApiData, "tools");
  structures.reports = processPermissionsData(rawApiData, "reports");
  structures.miscellaneous = processPermissionsData(
    rawApiData,
    "miscellaneous"
  );

  return structures;
}

/**
 * Check if cache is valid for the given group
 */
function isCacheValid(cache, groupId, groupName) {
  const isValid =
    cache.groupId === groupId &&
    cache.groupName === groupName &&
    cache.data !== null &&
    cache.data !== undefined;

  return isValid;
}
